import { Suspense, useState, type PropsWithChildren, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { twJoin, twMerge } from "tailwind-merge";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Icon } from "@/components/Icon";
import { Input, InputContainer } from "@/components/Input";
import { ProfileDropdown } from "@/components/ProfileDropdown";
import { Subtitle } from "@/components/Subtitle";
import { Water } from "@/components/Water";

export function ContentSubGrid({ children }: PropsWithChildren) {
  return (
    <main className="flex max-w-300 flex-col gap-4 px-4 md:col-[2_/_4] md:row-[1_/_4] md:grid md:grid-cols-subgrid md:grid-rows-subgrid">
      {children}
    </main>
  );
}

export function ContentFull({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <main className={twMerge("relative max-w-300 px-4 [grid-area:content]", className)}>{children}</main>;
}

export function ContentLayout({
  children,
  title,
  subtitle,
  wet = false,
}: PropsWithChildren<{ title?: ReactNode | undefined; subtitle?: ReactNode | undefined; wet?: boolean }>) {
  const { t } = useTranslation();
  const [navbar, setNavbar] = useState<HTMLDivElement | null>(null);
  const topSafeArea = navbar?.getBoundingClientRect().height;

  return (
    <div className="flex min-h-screen flex-col">
      <div
        className={twJoin(
          "relative grid grow",
          'md:[grid-template:"header_header_header_header"_auto_"left_content_content_right"_1fr_"footer_footer_footer_footer"_auto_/_1fr_minmax(auto,_53rem)_22rem_1fr]',
          '[grid-template:"header"_auto_"content"_1fr_"footer"_auto_/_1fr]',
        )}
        style={{ "--bale-safe-area-top": `${topSafeArea}px` }}
      >
        {wet && (
          <div className="relative col-span-full row-[2_/_-1] h-80 w-full">
            <Water width={100} height={25} start={15} end={15} spread={5} bends={3} />
          </div>
        )}
        <div
          className="sticky inset-x-0 top-0 z-10 flex min-h-[calc(env(safe-area-inset-top,_0)_+_4rem)] max-w-screen flex-row items-center bg-grass/50 pt-[env(safe-area-inset-top)] shadow-sm backdrop-blur-xs [grid-area:header]"
          ref={setNavbar}
        >
          <div className="hidden py-4 pl-4 md:block">
            <Link to="/overview" className="font-title">
              {t("title")}
            </Link>
          </div>
          <div className="mr-auto min-w-32 shrink pl-4 whitespace-nowrap md:pl-16">
            <div className="flex flex-row flex-wrap items-baseline gap-x-4">
              {!!title && <h1 className="truncate font-title">{title}</h1>}
              {!!subtitle && <Subtitle className="truncate">{subtitle}</Subtitle>}
            </div>
          </div>
          <div className="hidden px-4 sm:block md:px-16">
            <InputContainer className="relative flex flex-row">
              <Input type="search" placeholder={t("search")} />
              <div className="pointer-events-none flex flex-row items-center pr-2 text-placeholder">
                <Icon name="search" />
              </div>
            </InputContainer>
          </div>
          <ErrorBoundary>
            <Suspense>
              <ProfileDropdown />
            </Suspense>
          </ErrorBoundary>
        </div>
        {children}
        <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2 p-4 pt-12 pb-[calc(env(safe-area-inset-bottom,_0)_+_1rem))] font-hint [grid-area:footer]">
          {/* oxlint-disable-next-line jsx-no-literals */}
          <span>&copy; Bale 2026</span>
          <a href="/terms-of-service">{t("terms-of-service")}</a>
          <a href="/privacy-policy">{t("privacy-policy")}</a>
        </div>
      </div>
    </div>
  );
}
